import type {
  AttentionCategory,
  DeploymentAttempt,
  DeploymentOutcome,
  DeploymentServingState,
  EnvironmentHealth,
  InfrastructureHealth,
  LiveSiteHealth,
  OperationalAttentionItem,
  ProjectDeployment,
  ServingRelation,
  SiteState,
} from '../../api';
import { attentionCategoryLabel, attentionCategoryOrder } from '../../labels';

/**
 * The status model of the dashboard (CO-150). Three dimensions the server
 * derives and the UI never collapses into one word:
 *
 *  - the live site: operational, degraded, unavailable or unknown, from the
 *    site checks only;
 *  - the latest deployment: what happened to the attempt, and whether its
 *    release serves now;
 *  - infrastructure: pressure or faults underneath, which can coexist with
 *    a working site.
 *
 * Every surface — the Dashboard, the Projects list, the project header and
 * Overview, the deployment history and detail — reads them through these
 * functions, so one state cannot get two names or two colours. Nothing here
 * decides a state the server did not send: missing data is "unknown".
 */

/** The site as the UI shows it; a server older than CO-150 sends none. */
export function siteOf(health: Pick<EnvironmentHealth, 'site'> | null | undefined): LiveSiteHealth {
  return (
    health?.site ?? {
      state: 'unknown',
      reason: 'not_reported',
      headline: 'Not known: this environment’s site health has not been reported.',
      stale: false,
      verified: false,
      checks: 0,
      passing: 0,
      failing: 0,
      warning: 0,
      not_reported: 0,
      delayed: 0,
    }
  );
}

/** Infrastructure as the UI shows it. */
export function infrastructureOf(health: Pick<EnvironmentHealth, 'infrastructure'> | null | undefined): InfrastructureHealth {
  return health?.infrastructure ?? { level: 'unknown', headline: 'Not known', issues: 0 };
}

const siteRank: Record<SiteState, number> = { unavailable: 0, degraded: 1, unknown: 2, operational: 3 };

/** Orders sites worst first (presentation only). */
export function compareSite(a: SiteState | undefined, b: SiteState | undefined): number {
  return siteRank[a ?? 'unknown'] - siteRank[b ?? 'unknown'];
}

/**
 * The status kind and value an outcome renders with, from a record's own
 * status: the same mapping as domain.DeploymentOutcomeOf, for history rows
 * a server older than CO-150 sent without outcomes.
 */
export function outcomeOfStatus(status: string, failureReason?: string | null): DeploymentOutcome {
  switch (status) {
    case 'pending':
    case 'queued':
      return 'queued';
    case 'completed':
    case 'succeeded':
      return 'succeeded';
    case 'completed_with_failures':
      return 'succeeded_with_failures';
    case 'failed':
      return 'failed';
    case 'rolled_back':
      return 'rolled_back';
    case 'cancelled':
      return (failureReason ?? '').toLowerCase().startsWith('rehearsal') ? 'rehearsal' : 'cancelled';
    default:
      return 'deploying';
  }
}

/** A history row's outcome and serving relation: the server's, else the record's outcome and "unknown". */
export function rowState(deployment: ProjectDeployment, serving?: Record<string, DeploymentServingState> | null): DeploymentServingState {
  const known = serving?.[deployment.id];
  if (known) return known;
  const outcome = outcomeOfStatus(deployment.status, deployment.failure_reason);
  return { outcome, serving: outcome === 'queued' || outcome === 'deploying' ? 'pending' : 'unknown' };
}

/** True while an attempt has not finished. */
export function inProgress(outcome: DeploymentOutcome): boolean {
  return outcome === 'queued' || outcome === 'deploying';
}

/**
 * One sentence on what an attempt means for what serves. It never claims
 * visitors are unaffected: that is the site's answer, and only when its
 * checks are verified.
 */
export function attemptSentence(outcome: DeploymentOutcome, serving: ServingRelation, previousActive = false): string {
  switch (outcome) {
    case 'failed':
      if (serving === 'serving') return 'The failed release went live and is serving. Roll back or deploy a fix.';
      if (previousActive) return 'The update did not go live. The previous release remains active.';
      return 'The update did not go live.';
    case 'rolled_back':
      return 'The release was switched back; the one before it serves again.';
    case 'cancelled':
      return 'Cancelled before it went live. Nothing changed.';
    case 'rehearsal':
      return 'A rehearsal: nothing was changed on the server.';
    case 'queued':
      return 'Waiting to start. The current release keeps serving.';
    case 'deploying':
      return 'In progress. The current release keeps serving until traffic switches.';
    case 'succeeded_with_failures':
      return serving === 'serving' ? 'Serving now, but some of its checks failed.' : 'It went live with some checks failed; a newer release has replaced it.';
    case 'succeeded':
      if (serving === 'serving') return 'Serving now.';
      if (serving === 'superseded') return 'It went live; a newer release has replaced it.';
      return 'It went live.';
  }
}

/** The latest attempt in a sentence pair, for the Overview and the Projects list. */
export function latestAttemptOf(health: Pick<EnvironmentHealth, 'deployments'> | null | undefined): DeploymentAttempt | null {
  return health?.deployments?.latest_attempt ?? null;
}

/** Short text for a list cell: "Failed — previous release active". */
export function attemptShort(attempt: DeploymentAttempt): string {
  if (attempt.outcome === 'failed' && attempt.previous_active) return 'Previous release active';
  if (attempt.outcome === 'failed' && attempt.serving === 'serving') return 'The failed release is serving';
  if (attempt.outcome === 'failed') return 'Never went live';
  if (attempt.serving === 'serving') return 'Serving now';
  if (attempt.outcome === 'deploying') return 'In progress';
  if (attempt.outcome === 'queued') return 'Waiting to start';
  if (attempt.serving === 'superseded') return 'Replaced by a newer release';
  return '';
}

/** Category of an attention item; items from a server older than CO-150 are placed by kind. */
export function categoryOf(item: Pick<OperationalAttentionItem, 'category' | 'kind'>): AttentionCategory {
  if (item.category) return item.category;
  if (item.kind.startsWith('deployment_')) return 'deployment';
  if (item.kind.startsWith('backup_') || item.kind.startsWith('restore_')) return 'recovery';
  if (item.kind.startsWith('agent_') || item.kind === 'service_unreachable' || item.kind === 'host_capacity' || item.kind === 'drift' || item.kind === 'monitors_delayed') return 'infrastructure';
  if (item.kind.startsWith('certificate_')) return 'security';
  if (item.kind === 'incident' || item.kind === 'component_down' || item.kind === 'maintenance_kept') return 'site';
  if (item.kind === 'setup_failed' || item.kind === 'no_domain' || item.kind === 'component_needs_decision') return 'setup';
  return 'operations';
}

export interface AttentionGroup<T> {
  category: AttentionCategory;
  label: string;
  items: T[];
}

/** Groups attention items by category in the order a person reads them: the site first. */
export function groupByCategory<T extends Pick<OperationalAttentionItem, 'category' | 'kind'>>(items: T[]): AttentionGroup<T>[] {
  const groups = new Map<AttentionCategory, T[]>();
  for (const item of items) {
    const category = categoryOf(item);
    const list = groups.get(category) ?? [];
    list.push(item);
    groups.set(category, list);
  }
  return attentionCategoryOrder
    .filter((category) => groups.has(category))
    .map((category) => ({ category, label: attentionCategoryLabel[category] ?? category, items: groups.get(category)! }));
}

/** The site word for a level-only reader (servers' hosted environments from older servers). */
export function siteStateOrUnknown(state: SiteState | undefined | null): SiteState {
  return state ?? 'unknown';
}
